import { FormEvent, PointerEvent, useEffect, useMemo, useState } from "react";
import { FormRunner } from "@bjalon/form-runtime/runner";
import { compileForm } from "@bjalon/form-runtime/compiler";
import type { FormAnswers, FormAnswerValue } from "@bjalon/form-runtime";
import {
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  type User,
} from "firebase/auth";
import { auth, hasFirebaseConfig } from "../lib/firebase";
import {
  buildAttendanceId,
  createParticipantAttendance,
  readParticipantAttendance,
  resolveAttendanceWindow,
  type AttendanceWindow,
} from "../services/attendanceService";
import {
  listCurrentUserSessions,
  listSessionPublicDecks,
  listSessionPublicExercises,
  listSessionPublicQuestionnaires,
  readSessionPublicProjection,
} from "../services/accessProjectionService";
import {
  readParticipantExerciseAnswer,
  saveParticipantExerciseAnswer,
} from "../services/exerciseRuntimeService";
import {
  readParticipantQuestionnaireAnswer,
  saveParticipantQuestionnaireAnswer,
} from "../services/questionnaireRuntimeService";
import {
  clearLocalExerciseDraft,
  clearLocalQuestionnaireDraft,
  readLocalExerciseDraft,
  readLocalQuestionnaireDraft,
  readParticipantSessionCache,
  saveLocalExerciseDraft,
  saveLocalQuestionnaireDraft,
  saveParticipantSessionCache,
  type LocalExerciseDraft,
  type LocalQuestionnaireDraft,
} from "../services/offlineParticipantService";
import type {
  AttendanceRecord,
  ExerciseAnswerRecord,
  QuestionnaireAnswerRecord,
  SessionPublicDeck,
  SessionPublicExercise,
  SessionPublicQuestionnaire,
  SessionPublicProjection,
  UserSessionIndex,
} from "../types/access";
import { normalizeEmail } from "../utils/firestoreData";

function formatDate(value: Date | null): string {
  if (!value) {
    return "Non disponible";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(value);
}

function visiblePages(deck: SessionPublicDeck): number {
  return Math.min(deck.pageCount, Math.max(0, deck.visibleMaxPage));
}

function isFormAnswerValue(value: unknown): value is FormAnswerValue {
  return (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    value === null ||
    (Array.isArray(value) && value.every((item) => typeof item === "string"))
  );
}

function toFormAnswers(value: Record<string, unknown>): FormAnswers {
  return Object.fromEntries(
    Object.entries(value).filter((entry): entry is [string, FormAnswerValue] =>
      isFormAnswerValue(entry[1]),
    ),
  );
}

function canAccessQuestionnaire(questionnaire: SessionPublicQuestionnaire): boolean {
  return (
    questionnaire.runtimeStatus === "open" ||
    questionnaire.runtimeStatus === "closed_to_new_entries" ||
    questionnaire.runtimeStatus === "correction_visible"
  );
}

function canAccessExercise(exercise: SessionPublicExercise): boolean {
  return (
    exercise.runtimeStatus === "active" ||
    exercise.runtimeStatus === "passed" ||
    exercise.runtimeStatus === "correction_visible"
  );
}

function questionnaireStatusLabel(
  questionnaire: SessionPublicQuestionnaire,
): string {
  if (questionnaire.runtimeStatus === "open") {
    return "Ouvert";
  }

  if (questionnaire.runtimeStatus === "closed_to_new_entries") {
    return "Fermé aux nouveaux accès";
  }

  if (questionnaire.runtimeStatus === "closed") {
    return "Clôturé";
  }

  if (questionnaire.runtimeStatus === "correction_visible") {
    return "Correction visible";
  }

  return "Non ouvert";
}

function exerciseStatusLabel(exercise: SessionPublicExercise): string {
  if (exercise.runtimeStatus === "active") {
    return "Actif";
  }

  if (exercise.runtimeStatus === "passed") {
    return "Passé";
  }

  if (exercise.runtimeStatus === "correction_visible") {
    return "Correction visible";
  }

  return "Non activé";
}

type SignaturePoint = {
  x: number;
  y: number;
};

type SignatureStroke = readonly SignaturePoint[];

function strokePath(stroke: SignatureStroke): string {
  const [firstPoint, ...nextPoints] = stroke;

  if (!firstPoint) {
    return "";
  }

  return [
    `M ${firstPoint.x.toFixed(1)} ${firstPoint.y.toFixed(1)}`,
    ...nextPoints.map((point) => `L ${point.x.toFixed(1)} ${point.y.toFixed(1)}`),
  ].join(" ");
}

function signatureSvg(strokes: readonly SignatureStroke[]): string {
  const paths = strokes
    .map(strokePath)
    .filter(Boolean)
    .map((path) => `<path d="${path}" />`)
    .join("");

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 180"><g fill="none" stroke="#171717" stroke-width="4" stroke-linecap="round" stroke-linejoin="round">${paths}</g></svg>`;
}

function SignaturePad({
  disabled,
  onChange,
  strokes,
}: {
  disabled: boolean;
  onChange: (nextStrokes: SignatureStroke[]) => void;
  strokes: readonly SignatureStroke[];
}) {
  const [isDrawing, setIsDrawing] = useState(false);

  function pointFromEvent(event: PointerEvent<SVGSVGElement>): SignaturePoint {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * 600,
      y: ((event.clientY - rect.top) / rect.height) * 180,
    };
  }

  function handlePointerDown(event: PointerEvent<SVGSVGElement>) {
    if (disabled) {
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    setIsDrawing(true);
    onChange([...strokes, [pointFromEvent(event)]]);
  }

  function handlePointerMove(event: PointerEvent<SVGSVGElement>) {
    if (!isDrawing || disabled || strokes.length === 0) {
      return;
    }

    const nextStrokes = strokes.map((stroke) => [...stroke]);
    const lastStroke = nextStrokes[nextStrokes.length - 1];

    if (!lastStroke) {
      return;
    }

    lastStroke.push(pointFromEvent(event));
    onChange(nextStrokes);
  }

  function handlePointerUp(event: PointerEvent<SVGSVGElement>) {
    setIsDrawing(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }

  return (
    <svg
      aria-label="Zone de signature"
      className="signature-pad"
      onPointerDown={handlePointerDown}
      onPointerLeave={handlePointerUp}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      role="img"
      viewBox="0 0 600 180"
    >
      <rect height="180" width="600" />
      {strokes.map((stroke, index) => (
        <path d={strokePath(stroke)} key={index} />
      ))}
    </svg>
  );
}

export function ParticipantDashboard() {
  const [attendance, setAttendance] = useState<AttendanceRecord | null>(null);
  const [decks, setDecks] = useState<SessionPublicDeck[]>([]);
  const [email, setEmail] = useState("");
  const [exerciseAnswer, setExerciseAnswer] =
    useState<ExerciseAnswerRecord | null>(null);
  const [exerciseAnswerMarkdown, setExerciseAnswerMarkdown] = useState("");
  const [exerciseAnswersById, setExerciseAnswersById] = useState<
    Record<string, ExerciseAnswerRecord>
  >({});
  const [exercises, setExercises] = useState<SessionPublicExercise[]>([]);
  const [isAuthLoading, setIsAuthLoading] = useState(hasFirebaseConfig);
  const [isLoading, setIsLoading] = useState(false);
  const [isOnline, setIsOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  const [lastCacheSavedAt, setLastCacheSavedAt] = useState<Date | null>(null);
  const [localExerciseDraft, setLocalExerciseDraft] =
    useState<LocalExerciseDraft | null>(null);
  const [localQuestionnaireDraft, setLocalQuestionnaireDraft] =
    useState<LocalQuestionnaireDraft | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [projection, setProjection] = useState<SessionPublicProjection | null>(
    null,
  );
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [questionnaireAnswer, setQuestionnaireAnswer] =
    useState<QuestionnaireAnswerRecord | null>(null);
  const [questionnaireAnswersById, setQuestionnaireAnswersById] = useState<
    Record<string, QuestionnaireAnswerRecord>
  >({});
  const [questionnaireAnswers, setQuestionnaireAnswers] = useState<FormAnswers>(
    {},
  );
  const [questionnaires, setQuestionnaires] = useState<
    SessionPublicQuestionnaire[]
  >([]);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<
    string | null
  >(null);
  const [selectedExerciseId, setSelectedExerciseId] = useState<string | null>(
    null,
  );
  const [sessions, setSessions] = useState<UserSessionIndex[]>([]);
  const [signatureStrokes, setSignatureStrokes] = useState<SignatureStroke[]>([]);
  const [user, setUser] = useState<User | null>(null);

  const participantSessions = useMemo(
    () =>
      sessions.filter(
        (session) =>
          session.role === "participant" &&
          (session.status === "active" || session.status === "ended"),
      ),
    [sessions],
  );
  const selectedSession = useMemo(
    () =>
      participantSessions.find(
        (session) => session.sessionId === selectedSessionId,
      ) ?? null,
    [participantSessions, selectedSessionId],
  );
  const accessibleQuestionnaires = useMemo(
    () => questionnaires.filter(canAccessQuestionnaire),
    [questionnaires],
  );
  const accessibleExercises = useMemo(
    () => exercises.filter(canAccessExercise),
    [exercises],
  );
  const selectedQuestionnaire = useMemo(
    () =>
      accessibleQuestionnaires.find(
        (questionnaire) => questionnaire.id === selectedQuestionnaireId,
      ) ?? null,
    [accessibleQuestionnaires, selectedQuestionnaireId],
  );
  const selectedExercise = useMemo(
    () =>
      accessibleExercises.find((exercise) => exercise.id === selectedExerciseId) ??
      null,
    [accessibleExercises, selectedExerciseId],
  );
  const visibleSlidesCount = useMemo(
    () => decks.reduce((total, deck) => total + visiblePages(deck), 0),
    [decks],
  );
  const startedQuestionnairesCount = useMemo(
    () => Object.keys(questionnaireAnswersById).length,
    [questionnaireAnswersById],
  );
  const submittedQuestionnairesCount = useMemo(
    () =>
      Object.values(questionnaireAnswersById).filter(
        (answer) => answer.status === "submitted",
      ).length,
    [questionnaireAnswersById],
  );
  const submittedExercisesCount = useMemo(
    () =>
      Object.values(exerciseAnswersById).filter(
        (answer) => answer.status === "submitted",
      ).length,
    [exerciseAnswersById],
  );
  const visibleCorrectionsCount = useMemo(
    () =>
      questionnaires.filter(
        (questionnaire) => questionnaire.runtimeStatus === "correction_visible",
      ).length +
      exercises.filter((exercise) => exercise.correctionVisible).length,
    [exercises, questionnaires],
  );
  const compiledQuestionnaire = useMemo(() => {
    if (!selectedQuestionnaire?.yamlDefinition) {
      return null;
    }

    const result = compileForm(
      {
        content: selectedQuestionnaire.yamlDefinition,
        uri: `qorelia-session://${selectedQuestionnaire.sessionId}/${selectedQuestionnaire.id}.yaml`,
      },
      undefined,
      { mode: "strict" },
    );

    return result.status === "invalid" ? null : result.form;
  }, [selectedQuestionnaire]);
  const attendanceWindow = useMemo<AttendanceWindow | null>(() => {
    if (!projection) {
      return null;
    }

    return resolveAttendanceWindow({
      endAt: projection.publicEndAt,
      startAt: projection.publicStartAt,
      timezone: projection.timezone,
    });
  }, [projection]);

  useEffect(() => {
    if (!auth || !hasFirebaseConfig) {
      setIsAuthLoading(false);
      return undefined;
    }

    return onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
      setIsAuthLoading(false);
    });
  }, []);

  useEffect(() => {
    function handleOnline() {
      setIsOnline(true);
    }

    function handleOffline() {
      setIsOnline(false);
    }

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setDecks([]);
      setAttendance(null);
      setExerciseAnswer(null);
      setExerciseAnswerMarkdown("");
      setExerciseAnswersById({});
      setExercises([]);
      setLastCacheSavedAt(null);
      setLocalExerciseDraft(null);
      setLocalQuestionnaireDraft(null);
      setProjection(null);
      setQuestionnaireAnswer(null);
      setQuestionnaireAnswersById({});
      setQuestionnaireAnswers({});
      setQuestionnaires([]);
      setSelectedExerciseId(null);
      setSelectedQuestionnaireId(null);
      setSelectedSessionId(null);
      setSessions([]);
      return;
    }

    setIsLoading(true);
    setMessage(null);

    void listCurrentUserSessions(user.uid)
      .then((nextSessions) => {
        const nextParticipantSessions = nextSessions.filter(
          (session) =>
            session.role === "participant" &&
            (session.status === "active" || session.status === "ended"),
        );
        setSessions(nextSessions);
        setSelectedSessionId((currentId) => {
          if (
            currentId &&
            nextParticipantSessions.some(
              (session) => session.sessionId === currentId,
            )
          ) {
            return currentId;
          }

          return nextParticipantSessions.length === 1
            ? nextParticipantSessions[0]?.sessionId ?? null
            : null;
        });
      })
      .catch((error: unknown) => {
        console.error(error);
        setMessage("Impossible de charger vos sessions.");
      })
      .finally(() => setIsLoading(false));
  }, [user]);

  useEffect(() => {
    if (!selectedSessionId) {
      setAttendance(null);
      setDecks([]);
      setExerciseAnswer(null);
      setExerciseAnswerMarkdown("");
      setExerciseAnswersById({});
      setExercises([]);
      setLastCacheSavedAt(null);
      setLocalExerciseDraft(null);
      setLocalQuestionnaireDraft(null);
      setProjection(null);
      setQuestionnaireAnswer(null);
      setQuestionnaireAnswersById({});
      setQuestionnaireAnswers({});
      setQuestionnaires([]);
      setSelectedExerciseId(null);
      setSelectedQuestionnaireId(null);
      return;
    }

    setIsLoading(true);
    setMessage(null);
    const cachedDashboard = user
      ? readParticipantSessionCache({
          sessionId: selectedSessionId,
          uid: user.uid,
        })
      : null;

    if (cachedDashboard) {
      setProjection(cachedDashboard.projection);
      setDecks(cachedDashboard.decks);
      setExercises(cachedDashboard.exercises);
      setSelectedExerciseId((currentId) => {
        const nextAccessibleExercises =
          cachedDashboard.exercises.filter(canAccessExercise);

        if (
          currentId &&
          nextAccessibleExercises.some((exercise) => exercise.id === currentId)
        ) {
          return currentId;
        }

        return nextAccessibleExercises[0]?.id ?? null;
      });
      setQuestionnaires(cachedDashboard.questionnaires);
      setSelectedQuestionnaireId((currentId) => {
        const nextAccessibleQuestionnaires =
          cachedDashboard.questionnaires.filter(canAccessQuestionnaire);

        if (
          currentId &&
          nextAccessibleQuestionnaires.some(
            (questionnaire) => questionnaire.id === currentId,
          )
        ) {
          return currentId;
        }

        return nextAccessibleQuestionnaires[0]?.id ?? null;
      });
      setLastCacheSavedAt(cachedDashboard.savedAt);
    }

    void Promise.all([
      readSessionPublicProjection(selectedSessionId),
      listSessionPublicDecks(selectedSessionId),
      listSessionPublicExercises(selectedSessionId),
      listSessionPublicQuestionnaires(selectedSessionId),
    ])
      .then(([nextProjection, nextDecks, nextExercises, nextQuestionnaires]) => {
        if (user) {
          saveParticipantSessionCache({
            decks: nextDecks,
            exercises: nextExercises,
            projection: nextProjection,
            questionnaires: nextQuestionnaires,
            sessionId: selectedSessionId,
            uid: user.uid,
          });
          setLastCacheSavedAt(new Date());
        }
        setProjection(nextProjection);
        setDecks(nextDecks);
        setExercises(nextExercises);
        setSelectedExerciseId((currentId) => {
          const nextAccessibleExercises =
            nextExercises.filter(canAccessExercise);

          if (
            currentId &&
            nextAccessibleExercises.some((exercise) => exercise.id === currentId)
          ) {
            return currentId;
          }

          return nextAccessibleExercises[0]?.id ?? null;
        });
        setQuestionnaires(nextQuestionnaires);
        setSelectedQuestionnaireId((currentId) => {
          const nextAccessibleQuestionnaires =
            nextQuestionnaires.filter(canAccessQuestionnaire);

          if (
            currentId &&
            nextAccessibleQuestionnaires.some(
              (questionnaire) => questionnaire.id === currentId,
            )
          ) {
            return currentId;
          }

          return nextAccessibleQuestionnaires[0]?.id ?? null;
        });
        if (!nextProjection) {
          setMessage("Cette session n'est pas accessible.");
        }
      })
      .catch((error: unknown) => {
        console.error(error);
        setMessage(
          cachedDashboard
            ? "Connexion indisponible : affichage du dernier cache de lecture."
            : "Impossible de charger le dashboard de session.",
        );
      })
      .finally(() => setIsLoading(false));
  }, [selectedSessionId, user]);

  useEffect(() => {
    if (!attendanceWindow || !selectedSession?.participantSlotId) {
      setAttendance(null);
      return;
    }

    const attendanceId = buildAttendanceId({
      halfDay: attendanceWindow.halfDay,
      participantSlotId: selectedSession.participantSlotId,
      sessionDate: attendanceWindow.sessionDate,
    });

    void readParticipantAttendance({
      attendanceId,
      sessionId: selectedSession.sessionId,
    })
      .then(setAttendance)
      .catch((error: unknown) => {
        console.error(error);
        setMessage("Impossible de charger votre émargement.");
      });
  }, [attendanceWindow, selectedSession]);

  useEffect(() => {
    if (!selectedQuestionnaire || !selectedSession || !user) {
      setQuestionnaireAnswer(null);
      setQuestionnaireAnswers({});
      setLocalQuestionnaireDraft(null);
      return;
    }

    const localDraft = readLocalQuestionnaireDraft({
      questionnaireRuntimeId: selectedQuestionnaire.id,
      sessionId: selectedSession.sessionId,
      uid: user.uid,
    });
    setLocalQuestionnaireDraft(localDraft);

    void readParticipantQuestionnaireAnswer({
      questionnaireRuntimeId: selectedQuestionnaire.id,
      sessionId: selectedSession.sessionId,
      uid: user.uid,
    })
      .then((answer) => {
        setQuestionnaireAnswer(answer);
        setQuestionnaireAnswers(
          answer?.status === "submitted"
            ? toFormAnswers(answer.answersJson)
            : localDraft?.answers ?? (answer ? toFormAnswers(answer.answersJson) : {}),
        );
      })
      .catch((error: unknown) => {
        console.error(error);
        setMessage("Impossible de charger votre brouillon questionnaire.");
      });
  }, [selectedQuestionnaire, selectedSession, user]);

  useEffect(() => {
    if (!selectedSession || !user || accessibleQuestionnaires.length === 0) {
      setQuestionnaireAnswersById({});
      return;
    }

    void Promise.all(
      accessibleQuestionnaires.map((questionnaire) =>
        readParticipantQuestionnaireAnswer({
          questionnaireRuntimeId: questionnaire.id,
          sessionId: selectedSession.sessionId,
          uid: user.uid,
        }),
      ),
    )
      .then((answers) => {
        setQuestionnaireAnswersById(
          Object.fromEntries(
            answers
              .filter((answer): answer is QuestionnaireAnswerRecord => answer !== null)
              .map((answer) => [answer.questionnaireRuntimeId, answer]),
          ),
        );
      })
      .catch((error: unknown) => {
        console.error(error);
        setMessage("Impossible de charger vos réponses questionnaire.");
      });
  }, [accessibleQuestionnaires, selectedSession, user]);

  useEffect(() => {
    if (!selectedExercise || !selectedSession || !user) {
      setExerciseAnswer(null);
      setExerciseAnswerMarkdown("");
      setLocalExerciseDraft(null);
      return;
    }

    const localDraft = readLocalExerciseDraft({
      exerciseRuntimeId: selectedExercise.id,
      sessionId: selectedSession.sessionId,
      uid: user.uid,
    });
    setLocalExerciseDraft(localDraft);

    void readParticipantExerciseAnswer({
      exerciseRuntimeId: selectedExercise.id,
      sessionId: selectedSession.sessionId,
      uid: user.uid,
    })
      .then((answer) => {
        setExerciseAnswer(answer);
        setExerciseAnswerMarkdown(
          answer?.status === "submitted"
            ? answer.answerMarkdown
            : localDraft?.answerMarkdown ?? answer?.answerMarkdown ?? "",
        );
      })
      .catch((error: unknown) => {
        console.error(error);
        setMessage("Impossible de charger votre brouillon exercice.");
      });
  }, [selectedExercise, selectedSession, user]);

  useEffect(() => {
    if (!selectedSession || !user || accessibleExercises.length === 0) {
      setExerciseAnswersById({});
      return;
    }

    void Promise.all(
      accessibleExercises.map((exercise) =>
        readParticipantExerciseAnswer({
          exerciseRuntimeId: exercise.id,
          sessionId: selectedSession.sessionId,
          uid: user.uid,
        }),
      ),
    )
      .then((answers) => {
        setExerciseAnswersById(
          Object.fromEntries(
            answers
              .filter((answer): answer is ExerciseAnswerRecord => answer !== null)
              .map((answer) => [answer.exerciseRuntimeId, answer]),
          ),
        );
      })
      .catch((error: unknown) => {
        console.error(error);
        setMessage("Impossible de charger vos réponses exercice.");
      });
  }, [accessibleExercises, selectedSession, user]);

  async function handleLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!auth) {
      setMessage("La configuration Firebase est incomplète.");
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      await signInWithEmailAndPassword(auth, normalizeEmail(email), password);
      setPassword("");
    } catch (error) {
      console.error(error);
      setMessage("Connexion impossible avec ces identifiants.");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleResetPassword() {
    if (!auth) {
      setMessage("La configuration Firebase est incomplète.");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, normalizeEmail(email), {
        url: `${window.location.origin}/#/participant`,
      });
      setMessage("Email de réinitialisation envoyé si le compte existe.");
    } catch (error) {
      console.error(error);
      setMessage("Impossible d'envoyer l'email de réinitialisation.");
    }
  }

  async function handleSignOut() {
    if (!auth) {
      return;
    }

    await signOut(auth);
  }

  async function handleSubmitAttendance() {
    if (!isOnline) {
      setMessage(
        "Émargement refusé hors ligne : la signature doit être confirmée par le serveur.",
      );
      return;
    }

    if (
      !attendanceWindow ||
      !selectedSession?.participantSlotId ||
      !user ||
      signatureStrokes.length === 0
    ) {
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const nextAttendance = await createParticipantAttendance({
        attendanceWindow,
        participantSlotId: selectedSession.participantSlotId,
        sessionId: selectedSession.sessionId,
        signatureSvg: signatureSvg(signatureStrokes),
        uid: user.uid,
      });
      setAttendance(nextAttendance);
      setSignatureStrokes([]);
      setMessage("Émargement enregistré.");
    } catch (error) {
      console.error(error);
      setMessage("Impossible d'enregistrer cet émargement.");
    } finally {
      setIsLoading(false);
    }
  }

  function saveQuestionnaireDraftLocally(): boolean {
    if (!selectedQuestionnaire || !selectedSession || !user) {
      return false;
    }

    const draft = saveLocalQuestionnaireDraft({
      answers: questionnaireAnswers,
      questionnaireRuntimeId: selectedQuestionnaire.id,
      sessionId: selectedSession.sessionId,
      uid: user.uid,
    });

    setLocalQuestionnaireDraft(draft);
    return draft !== null;
  }

  function saveExerciseDraftLocally(): boolean {
    if (!selectedExercise || !selectedSession || !user) {
      return false;
    }

    const draft = saveLocalExerciseDraft({
      answerMarkdown: exerciseAnswerMarkdown,
      exerciseRuntimeId: selectedExercise.id,
      sessionId: selectedSession.sessionId,
      uid: user.uid,
    });

    setLocalExerciseDraft(draft);
    return draft !== null;
  }

  async function handleSaveQuestionnaire(status: "draft" | "submitted") {
    if (
      !selectedQuestionnaire ||
      !selectedSession?.participantSlotId ||
      !user ||
      questionnaireAnswer?.status === "submitted"
    ) {
      return;
    }

    if (!isOnline) {
      if (status === "draft" && saveQuestionnaireDraftLocally()) {
        setMessage(
          "Brouillon questionnaire enregistré localement. Synchronisez-le quand la connexion revient.",
        );
      } else {
        setMessage("Soumission impossible hors ligne.");
      }
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const nextAnswer = await saveParticipantQuestionnaireAnswer({
        answers: questionnaireAnswers,
        participantSlotId: selectedSession.participantSlotId,
        questionnaire: selectedQuestionnaire,
        sessionId: selectedSession.sessionId,
        status,
        uid: user.uid,
      });
      setQuestionnaireAnswer(nextAnswer);
      setQuestionnaireAnswersById((current) => ({
        ...current,
        [nextAnswer.questionnaireRuntimeId]: nextAnswer,
      }));
      setQuestionnaireAnswers(toFormAnswers(nextAnswer.answersJson));
      clearLocalQuestionnaireDraft({
        questionnaireRuntimeId: selectedQuestionnaire.id,
        sessionId: selectedSession.sessionId,
        uid: user.uid,
      });
      setLocalQuestionnaireDraft(null);
      setMessage(
        status === "submitted"
          ? "Réponse questionnaire soumise."
          : "Brouillon questionnaire enregistré.",
      );
    } catch (error: unknown) {
      console.error(error);
      if (status === "draft" && saveQuestionnaireDraftLocally()) {
        setMessage(
          "Connexion indisponible : brouillon questionnaire conservé localement.",
        );
      } else {
        setMessage("Impossible d'enregistrer ce questionnaire.");
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSyncQuestionnaireDraft() {
    if (!localQuestionnaireDraft) {
      return;
    }

    await handleSaveQuestionnaire("draft");
  }

  async function handleSaveExercise(status: "draft" | "submitted") {
    if (
      !selectedExercise ||
      !selectedSession?.participantSlotId ||
      !user ||
      selectedExercise.responseMode !== "markdown" ||
      selectedExercise.runtimeStatus !== "active" ||
      exerciseAnswer?.status === "submitted"
    ) {
      return;
    }

    if (!isOnline) {
      if (status === "draft" && saveExerciseDraftLocally()) {
        setMessage(
          "Brouillon exercice enregistré localement. Synchronisez-le quand la connexion revient.",
        );
      } else {
        setMessage("Soumission impossible hors ligne.");
      }
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const nextAnswer = await saveParticipantExerciseAnswer({
        answerMarkdown: exerciseAnswerMarkdown,
        exercise: selectedExercise,
        participantSlotId: selectedSession.participantSlotId,
        sessionId: selectedSession.sessionId,
        status,
        uid: user.uid,
      });
      setExerciseAnswer(nextAnswer);
      setExerciseAnswersById((current) => ({
        ...current,
        [nextAnswer.exerciseRuntimeId]: nextAnswer,
      }));
      setExerciseAnswerMarkdown(nextAnswer.answerMarkdown);
      clearLocalExerciseDraft({
        exerciseRuntimeId: selectedExercise.id,
        sessionId: selectedSession.sessionId,
        uid: user.uid,
      });
      setLocalExerciseDraft(null);
      setMessage(
        status === "submitted"
          ? "Réponse exercice soumise."
          : "Brouillon exercice enregistré.",
      );
    } catch (error: unknown) {
      console.error(error);
      if (status === "draft" && saveExerciseDraftLocally()) {
        setMessage("Connexion indisponible : brouillon exercice conservé localement.");
      } else {
        setMessage("Impossible d'enregistrer cet exercice.");
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSyncExerciseDraft() {
    if (!localExerciseDraft) {
      return;
    }

    await handleSaveExercise("draft");
  }

  if (isAuthLoading) {
    return (
      <main className="public-shell">
        <section className="auth-panel">
          <p className="eyebrow">Participant</p>
          <h1>Chargement</h1>
          <p>Vérification de votre session.</p>
        </section>
      </main>
    );
  }

  if (!user) {
    return (
      <main className="public-shell">
        <section className="auth-panel">
          <p className="eyebrow">Participant</p>
          <h1>Mes formations</h1>
          <form className="stack" onSubmit={handleLogin}>
            <label className="field">
              <span>Email</span>
              <input
                autoComplete="email"
                inputMode="email"
                onChange={(event) => setEmail(event.target.value)}
                required
                type="email"
                value={email}
              />
            </label>
            <label className="field">
              <span>Mot de passe</span>
              <input
                autoComplete="current-password"
                onChange={(event) => setPassword(event.target.value)}
                required
                type="password"
                value={password}
              />
            </label>
            <div className="actions">
              <button
                className="button button-primary"
                disabled={isLoading}
                type="submit"
              >
                Se connecter
              </button>
              <button
                className="button button-secondary"
                disabled={isLoading || !email.trim()}
                onClick={handleResetPassword}
                type="button"
              >
                Mot de passe oublié
              </button>
            </div>
          </form>
          {message ? (
            <div className="alert alert-info" role="status">
              {message}
            </div>
          ) : null}
        </section>
      </main>
    );
  }

  return (
    <main className="public-shell">
      <header className="topbar">
        <div>
          <p className="eyebrow">Participant</p>
          <h1>Dashboard session</h1>
          <div className="network-status">
            <span className={isOnline ? "status status-accepted" : "status status-revoked"}>
              {isOnline ? "En ligne" : "Hors ligne"}
            </span>
            {lastCacheSavedAt ? (
              <span className="status status-pending">
                Cache {formatDate(lastCacheSavedAt)}
              </span>
            ) : null}
          </div>
        </div>
        <button
          className="button button-secondary"
          onClick={handleSignOut}
          type="button"
        >
          Déconnexion
        </button>
      </header>

      <section className="public-content">
        <article className="panel">
          <p className="eyebrow">Mes sessions</p>
          <h2>Accès participant</h2>
          {isLoading ? <p>Chargement...</p> : null}
          {participantSessions.length > 0 ? (
            <ul className="resource-list">
              {participantSessions.map((session) => (
                <li key={session.sessionId}>
                  <button
                    className={
                      selectedSessionId === session.sessionId
                        ? "resource-button resource-button-active"
                        : "resource-button"
                    }
                    onClick={() => setSelectedSessionId(session.sessionId)}
                    type="button"
                  >
                    <strong>{session.title}</strong>
                    <span>
                      {session.status} - {formatDate(session.publicStartAt)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p>Aucune session participant active pour ce compte.</p>
          )}
        </article>

        <article className="panel">
          <p className="eyebrow">Session</p>
          <h2>{projection?.title ?? "Sélectionner une session"}</h2>
          {projection ? (
            <div className="details-list">
              <div>
                <dt>Statut</dt>
                <dd>{projection.status}</dd>
              </div>
              <div>
                <dt>Début</dt>
                <dd>{formatDate(projection.publicStartAt)}</dd>
              </div>
              <div>
                <dt>Fin</dt>
                <dd>{formatDate(projection.publicEndAt)}</dd>
              </div>
              <div>
                <dt>Résumé live</dt>
                <dd>{projection.liveSummary || "Accueil"}</dd>
              </div>
            </div>
          ) : (
            <p>Choisissez une session pour afficher ses informations publiques.</p>
          )}
        </article>
      </section>

      {projection ? (
        <section className="panel panel-wide">
          <p className="eyebrow">Vue d'ensemble</p>
          <h2>Votre progression</h2>
          <div className="participant-summary-grid">
            <div>
              <strong>{visibleSlidesCount}</strong>
              <span>slide(s) visible(s)</span>
            </div>
            <div>
              <strong>
                {submittedQuestionnairesCount}/{accessibleQuestionnaires.length}
              </strong>
              <span>questionnaire(s) soumis</span>
            </div>
            <div>
              <strong>
                {submittedExercisesCount}/{accessibleExercises.length}
              </strong>
              <span>exercice(s) soumis</span>
            </div>
            <div>
              <strong>{visibleCorrectionsCount}</strong>
              <span>correction(s) visible(s)</span>
            </div>
          </div>
          <div className="capability-strip">
            <span>{startedQuestionnairesCount} questionnaire(s) commencé(s)</span>
            <span>{attendance ? "Émargement enregistré" : "Émargement à faire"}</span>
            <span>Accès {selectedSession?.status ?? projection.status}</span>
            <span>{isOnline ? "Synchronisation disponible" : "Mode dégradé"}</span>
          </div>
        </section>
      ) : null}

      <section className="panel panel-wide">
        <p className="eyebrow">Émargement</p>
        <h2>
          {attendanceWindow
            ? `${attendanceWindow.label} - ${attendanceWindow.displayDate}`
            : "Signature"}
        </h2>
        {attendance ? (
          <div className="alert alert-success" role="status">
            Signature enregistrée pour cette demi-journée.
          </div>
        ) : selectedSession?.participantSlotId && attendanceWindow ? (
          <div className="signature-area">
            <SignaturePad
              disabled={isLoading || !isOnline}
              onChange={setSignatureStrokes}
              strokes={signatureStrokes}
            />
            {!isOnline ? (
              <div className="alert alert-warning" role="status">
                L'émargement n'est pas disponible hors ligne.
              </div>
            ) : null}
            <div className="actions">
              <button
                className="button button-primary"
                disabled={isLoading || !isOnline || signatureStrokes.length === 0}
                onClick={handleSubmitAttendance}
                type="button"
              >
                Signer l'émargement
              </button>
              <button
                className="button button-secondary"
                disabled={isLoading || signatureStrokes.length === 0}
                onClick={() => setSignatureStrokes([])}
                type="button"
              >
                Effacer
              </button>
            </div>
          </div>
        ) : (
          <p>Votre accès participant n'est pas encore rattaché à un slot.</p>
        )}
      </section>

      <section className="panel panel-wide">
        <p className="eyebrow">Supports</p>
        <h2>Slides visibles</h2>
        {decks.length > 0 ? (
          <ul className="deck-access-list">
            {decks.map((deck) => {
              const count = visiblePages(deck);
              return (
                <li key={deck.id}>
                  <div>
                    <strong>{deck.title}</strong>
                    <span>
                      {count} / {deck.pageCount} page(s) visible(s)
                    </span>
                  </div>
                  <div className="slide-chip-grid">
                    {Array.from({ length: count }, (_, index) => (
                      <span className="slide-chip" key={`${deck.id}-${index}`}>
                        Page {index + 1}
                      </span>
                    ))}
                    {count === 0 ? (
                      <span className="slide-chip slide-chip-muted">
                        Aucune page ouverte
                      </span>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p>Aucun support publié dans cette session.</p>
        )}
      </section>

      <section className="panel panel-wide">
        <p className="eyebrow">Questionnaires</p>
        <h2>Questionnaires ouverts</h2>
        {accessibleQuestionnaires.length > 0 ? (
          <div className="questionnaire-runtime-layout">
            <ul className="resource-list">
              {accessibleQuestionnaires.map((questionnaire) => {
                const answer = questionnaireAnswersById[questionnaire.id];
                return (
                  <li key={questionnaire.id}>
                    <button
                      className={
                        selectedQuestionnaireId === questionnaire.id
                          ? "resource-button resource-button-active"
                          : "resource-button"
                      }
                      onClick={() => setSelectedQuestionnaireId(questionnaire.id)}
                      type="button"
                    >
                      <strong>{questionnaire.title}</strong>
                      <span>
                        {questionnaireStatusLabel(questionnaire)}
                        {answer ? ` - ${answer.status}` : ""}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="questionnaire-runner">
              {selectedQuestionnaire && compiledQuestionnaire ? (
                <>
                  <div className="capability-strip">
                    <span>{selectedQuestionnaire.runtimeStatus}</span>
                    {questionnaireAnswer?.status ? (
                      <span>Réponse : {questionnaireAnswer.status}</span>
                    ) : (
                      <span>Non commencé</span>
                    )}
                    {localQuestionnaireDraft ? (
                      <span>Local : {formatDate(localQuestionnaireDraft.savedAt)}</span>
                    ) : null}
                  </div>
                  <FormRunner
                    answers={questionnaireAnswers}
                    disabled={
                      isLoading ||
                      questionnaireAnswer?.status === "submitted" ||
                      selectedQuestionnaire.runtimeStatus === "correction_visible"
                    }
                    form={compiledQuestionnaire}
                    labels={{
                      next: "Suivant",
                      previous: "Précédent",
                      submit: "Valider",
                      submitted: "Réponse prête",
                    }}
                    onAnswersChange={setQuestionnaireAnswers}
                    onSubmit={() => void handleSaveQuestionnaire("submitted")}
                    viewMode="participant"
                  />
                  <div className="actions">
                    <button
                      className="button button-secondary"
                      disabled={
                        isLoading ||
                        questionnaireAnswer?.status === "submitted" ||
                        selectedQuestionnaire.runtimeStatus !== "open"
                      }
                      onClick={() => void handleSaveQuestionnaire("draft")}
                      type="button"
                    >
                      Enregistrer le brouillon
                    </button>
                    <button
                      className="button button-secondary"
                      disabled={
                        isLoading ||
                        !isOnline ||
                        !localQuestionnaireDraft ||
                        questionnaireAnswer?.status === "submitted"
                      }
                      onClick={() => void handleSyncQuestionnaireDraft()}
                      type="button"
                    >
                      Synchroniser le brouillon local
                    </button>
                    <button
                      className="button button-primary"
                      disabled={
                        isLoading ||
                        !isOnline ||
                        questionnaireAnswer?.status === "submitted" ||
                        !(
                          selectedQuestionnaire.runtimeStatus === "open" ||
                          selectedQuestionnaire.runtimeStatus ===
                            "closed_to_new_entries"
                        )
                      }
                      onClick={() => void handleSaveQuestionnaire("submitted")}
                      type="button"
                    >
                      Soumettre
                    </button>
                  </div>
                </>
              ) : (
                <p>Ce questionnaire n'est pas encore disponible au rendu.</p>
              )}
            </div>
          </div>
        ) : (
          <p>Aucun questionnaire ouvert pour cette session.</p>
        )}
      </section>

      <section className="panel panel-wide">
        <p className="eyebrow">Exercices</p>
        <h2>Exercices actifs</h2>
        {accessibleExercises.length > 0 ? (
          <div className="questionnaire-runtime-layout">
            <ul className="resource-list">
              {accessibleExercises.map((exercise) => {
                const answer = exerciseAnswersById[exercise.id];
                return (
                  <li key={exercise.id}>
                    <button
                      className={
                        selectedExerciseId === exercise.id
                          ? "resource-button resource-button-active"
                          : "resource-button"
                      }
                      onClick={() => setSelectedExerciseId(exercise.id)}
                      type="button"
                    >
                      <strong>{exercise.title}</strong>
                      <span>
                        {exerciseStatusLabel(exercise)} - mode{" "}
                        {exercise.responseMode}
                        {answer ? ` - ${answer.status}` : ""}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="questionnaire-runner">
              {selectedExercise ? (
                <>
                  <div className="capability-strip">
                    <span>{selectedExercise.runtimeStatus}</span>
                    <span>Mode : {selectedExercise.responseMode}</span>
                    {exerciseAnswer?.status ? (
                      <span>Réponse : {exerciseAnswer.status}</span>
                    ) : null}
                    {localExerciseDraft ? (
                      <span>Local : {formatDate(localExerciseDraft.savedAt)}</span>
                    ) : null}
                  </div>
                  <div className="markdown-block">
                    <h3>Énoncé</h3>
                    <p>{selectedExercise.statementMarkdown}</p>
                  </div>
                  {selectedExercise.responseMode === "markdown" ? (
                    <>
                      <label className="field">
                        <span>Votre réponse</span>
                        <textarea
                          disabled={
                            isLoading ||
                            selectedExercise.runtimeStatus !== "active" ||
                            exerciseAnswer?.status === "submitted"
                          }
                          onChange={(event) =>
                            setExerciseAnswerMarkdown(event.target.value)
                          }
                          value={exerciseAnswerMarkdown}
                        />
                      </label>
                      <div className="actions">
                        <button
                          className="button button-secondary"
                          disabled={
                            isLoading ||
                            selectedExercise.runtimeStatus !== "active" ||
                            exerciseAnswer?.status === "submitted"
                          }
                          onClick={() => void handleSaveExercise("draft")}
                          type="button"
                        >
                          Enregistrer le brouillon
                        </button>
                        <button
                          className="button button-secondary"
                          disabled={
                            isLoading ||
                            !isOnline ||
                            !localExerciseDraft ||
                            selectedExercise.runtimeStatus !== "active" ||
                            exerciseAnswer?.status === "submitted"
                          }
                          onClick={() => void handleSyncExerciseDraft()}
                          type="button"
                        >
                          Synchroniser le brouillon local
                        </button>
                        <button
                          className="button button-primary"
                          disabled={
                            isLoading ||
                            !isOnline ||
                            selectedExercise.runtimeStatus !== "active" ||
                            exerciseAnswer?.status === "submitted"
                          }
                          onClick={() => void handleSaveExercise("submitted")}
                          type="button"
                        >
                          Soumettre
                        </button>
                      </div>
                    </>
                  ) : (
                    <p>Aucune réponse écrite n'est attendue pour cet exercice.</p>
                  )}
                  {selectedExercise.correctionVisible ? (
                    <div className="markdown-block">
                      <h3>Correction</h3>
                      <p>
                        {selectedExercise.correctionMarkdown ||
                          selectedExercise.correctionDeckFragment ||
                          "Correction publiée."}
                      </p>
                    </div>
                  ) : null}
                </>
              ) : (
                <p>Sélectionnez un exercice actif.</p>
              )}
            </div>
          </div>
        ) : (
          <p>Aucun exercice actif pour cette session.</p>
        )}
      </section>

      <section className="panel panel-wide">
        <p className="eyebrow">Réponses personnelles</p>
        <h2>Historique de vos travaux</h2>
        {Object.keys(questionnaireAnswersById).length > 0 ||
        Object.keys(exerciseAnswersById).length > 0 ? (
          <div className="personal-work-grid">
            <div className="markdown-block">
              <h3>Questionnaires</h3>
              {Object.values(questionnaireAnswersById).length > 0 ? (
                <ul className="compact-list">
                  {Object.values(questionnaireAnswersById).map((answer) => {
                    const questionnaire = questionnaires.find(
                      (item) => item.id === answer.questionnaireRuntimeId,
                    );
                    return (
                      <li key={answer.id}>
                        <span>{questionnaire?.title ?? answer.questionnaireRuntimeId}</span>
                        <small>{answer.status}</small>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p>Aucune réponse questionnaire enregistrée.</p>
              )}
            </div>
            <div className="markdown-block">
              <h3>Exercices</h3>
              {Object.values(exerciseAnswersById).length > 0 ? (
                <ul className="compact-list">
                  {Object.values(exerciseAnswersById).map((answer) => {
                    const exercise = exercises.find(
                      (item) => item.id === answer.exerciseRuntimeId,
                    );
                    return (
                      <li key={answer.id}>
                        <span>{exercise?.title ?? answer.exerciseRuntimeId}</span>
                        <small>{answer.status}</small>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p>Aucune réponse exercice enregistrée.</p>
              )}
            </div>
          </div>
        ) : (
          <p>Aucun travail personnel enregistré pour l'instant.</p>
        )}
      </section>

      <section className="panel panel-wide">
        <p className="eyebrow">Corrections</p>
        <h2>Corrections visibles</h2>
        {visibleCorrectionsCount > 0 ? (
          <div className="personal-work-grid">
            {questionnaires
              .filter(
                (questionnaire) =>
                  questionnaire.runtimeStatus === "correction_visible",
              )
              .map((questionnaire) => (
                <div className="markdown-block" key={questionnaire.id}>
                  <h3>{questionnaire.title}</h3>
                  <p>Correction questionnaire publiée.</p>
                </div>
              ))}
            {exercises
              .filter((exercise) => exercise.correctionVisible)
              .map((exercise) => (
                <div className="markdown-block" key={exercise.id}>
                  <h3>{exercise.title}</h3>
                  <p>
                    {exercise.correctionMarkdown ||
                      exercise.correctionDeckFragment ||
                      "Correction publiée."}
                  </p>
                </div>
              ))}
          </div>
        ) : (
          <p>Aucune correction visible pour cette session.</p>
        )}
      </section>

      {message ? (
        <div className="alert alert-info" role="status">
          {message}
        </div>
      ) : null}
    </main>
  );
}
